// ── "The system is being updated" — the pure half (Bugify deploy-now, owner 2026-11-03) ───────────────────────────
// The owner's ask: while a deploy runs, the people using the system must see that it is being updated, so nobody takes
// a restart for a dead system. deploy.sh writes build/deploy-status.json on the server it deploys (scripts/
// deploy-status.sh): {v:1, state:'deploying'|'restarting'|'idle', build:'main.<hex>.js', startedAt, expiresAt}, every
// time in ms by the SERVER's clock. Every page of every installation reads that file (services/apiHealth.ts) and draws
// the notice (components/DeployNotice.tsx).
// This file is the part that decides, kept free of React, the DOM, i18n and the API client, because the server's test
// suite (server/src/scripts/testBugifyReleaseNow.ts) requires it as it is.
//
// Two rules keep the notice honest:
//   • A busy file that has outlived itself is idle. A deploy that died between its first write and its last would
//     otherwise leave "updating" on every screen for ever — so expiresAt, and the file's own age, end it, judged by the
//     server's clock (the response's Date header) whenever there is one, since a laptop's clock can be hours off.
//   • An idle file is never stale. Its build names what the server serves now, which is how a page that has been open
//     since before a deploy learns that a newer version is waiting (isNewBuild).

export type DeployState = 'idle' | 'deploying' | 'restarting';
export interface DeployStatus { state: DeployState; build: string; startedAt: number; stale: boolean; }
export const DEPLOY_STATUS_MAX_AGE_MS = 15 * 60_000;

const STATES: DeployState[] = ['idle', 'deploying', 'restarting'];
// The shape deploy-status.sh reads from the live index.html (a real build's hash is hex, as utils/appUpdate.tsx reads
// the page's own script tag); anything else in the field is dropped, since the file is public.
const BUILD_NAME = /^main\.[0-9A-Za-z_-]{1,64}\.js$/;
const IDLE: DeployStatus = { state: 'idle', build: '', startedAt: 0, stale: false };

const num = (v: any): number => (typeof v === 'number' && isFinite(v) ? v : 0);
const httpDate = (v?: string | null): number => {
  const ms = v ? Date.parse(v) : NaN;
  return isFinite(ms) ? ms : NaN;
};

/** Missing/HTML/invalid JSON/v!==1/unknown state → {state:'idle', build:'', startedAt:0, stale:false}.
 *  serverNow = Date header (else clientNowMs ?? Date.now()). A NON-IDLE file is stale when serverNow > expiresAt or
 *  serverNow − Last-Modified > MAX_AGE (that check skipped without the header); a stale non-idle file is returned as
 *  {state:'idle', stale:true, build kept}. An idle file is never stale and always keeps its build. */
export function parseDeployStatus(text: string, headers: { date?: string | null; lastModified?: string | null }, clientNowMs?: number): DeployStatus {
  let o: any = null;
  try { o = JSON.parse(String(text || '')); } catch { return { ...IDLE }; }
  if (!o || typeof o !== 'object' || o.v !== 1 || !STATES.includes(o.state)) return { ...IDLE };
  const build = typeof o.build === 'string' && BUILD_NAME.test(o.build) ? o.build : '';
  const startedAt = num(o.startedAt);
  if (o.state === 'idle') return { state: 'idle', build, startedAt, stale: false };
  const date = httpDate(headers && headers.date);
  const serverNow = isFinite(date) ? date : (typeof clientNowMs === 'number' && isFinite(clientNowMs) ? clientNowMs : Date.now());
  // A busy file with no expiry of its own is treated as expired: nothing may keep the notice up for ever.
  const expiresAt = num(o.expiresAt);
  const modified = httpDate(headers && headers.lastModified);
  const stale = serverNow > expiresAt || (isFinite(modified) && serverNow - modified > DEPLOY_STATUS_MAX_AGE_MS);
  return stale ? { state: 'idle', build, startedAt, stale: true } : { state: o.state, build, startedAt, stale: false };
}

/** true when status.build and loaded are both non-empty, differ, and status.build !== reloadedFor. */
export function isNewBuild(status: DeployStatus, loaded: string, reloadedFor: string): boolean {
  const live = (status && status.build) || '';
  return !!live && !!loaded && live !== loaded && live !== reloadedFor;
}

/** true for a fetch failure (any error except name==='AbortError') or status 502/503/504 with payload == null. */
export function isApiDownSignal(e: any): boolean {
  if (!e) return false;
  if (e.name === 'AbortError') return false;
  if (typeof e.status === 'number') return (e.status === 502 || e.status === 503 || e.status === 504) && e.payload == null;
  return true;
}

/** How far a bar with nothing to measure should be: quick at first, then ever slower, and never full — a restart that
 *  takes three minutes instead of one must still look alive, never finished. */
export function estimatedPct(elapsedMs: number): number {
  const t = typeof elapsedMs === 'number' && isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  return Math.min(95, Math.max(0, Math.round(95 * (1 - Math.exp(-t / 30000)))));
}

/** "m:ss" — minutes are not wrapped into hours (a 65-minute wait reads 65:00). */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor((typeof ms === 'number' && isFinite(ms) ? ms : 0) / 1000));
  const sec = s % 60;
  return `${Math.floor(s / 60)}:${sec < 10 ? '0' : ''}${sec}`;
}

// ── Is the API back? ─────────────────────────────────────────────────────────────────────────────────────────────────
// One failed request starts probing GET /api/health; the first answer after ANY trouble is "back" (slf:api-back), which
// is what lets the sign-in check, the portal profile and everything else that gave up try again. Only the second failed
// probe in a row counts as down — a single blip must not throw a blocking card over somebody's work.
export interface ProbeState { probing: boolean; fails: number; down: boolean; since: number; owedBack: boolean; }
export const PROBE_IDLE: ProbeState = { probing: false, fails: 0, down: false, since: 0, owedBack: false };

/** reportApiTrouble: start probing; owedBack = true; since = since || nowMs. */
export function probeTrouble(s: ProbeState, nowMs: number): ProbeState {
  return { ...s, probing: true, owedBack: true, since: s.since || nowMs };
}

/** One probe result. fail → fails+1, down = fails >= 2. ok → emitBack = s.owedBack, state = PROBE_IDLE, stop = true. */
export function probeStep(s: ProbeState, ok: boolean, nowMs: number): { state: ProbeState; emitBack: boolean; stop: boolean } {
  if (ok) return { state: { ...PROBE_IDLE }, emitBack: !!s.owedBack, stop: true };
  const fails = s.fails + 1;
  return { state: { ...s, probing: true, fails, down: s.down || fails >= 2, since: s.since || nowMs }, emitBack: false, stop: false };
}
